import { dateToCurrentPlainDateTime } from "@redotech/util/temporal";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { TimeUnit } from "../time";

export const subscriptionDelayTimeUnits = [
  TimeUnit.YEAR,
  TimeUnit.MONTH,
  TimeUnit.WEEK,
  TimeUnit.DAY,
] as const;
export const subscriptionDelayTimeUnitSchema = createEnum(
  subscriptionDelayTimeUnits,
  { enumName: "SubscriptionDelayTimeUnits" },
);
export type SubscriptionDelayTimeUnit = z.infer<
  typeof subscriptionDelayTimeUnitSchema
>;

export enum SubscriptionDateFormat {
  FULL = "full",
  ABBREVIATED = "abbreviated",
  RELATIVE = "relative",
}

const pluralRules = new Intl.PluralRules("en", { type: "ordinal" });
const suffixes: Partial<Record<Intl.LDMLPluralRule, string>> = {
  one: "st",
  two: "nd",
  few: "rd",
  other: "th",
};
function formatOrdinal(n: number): string {
  const rule = pluralRules.select(n);
  const suffix = suffixes[rule] ?? "th"; // coalesce should never happen, but just in case
  return `${n}${suffix}`;
}

export function formatDate(date: Date, format: SubscriptionDateFormat): string {
  switch (format) {
    case SubscriptionDateFormat.FULL: {
      const month = date.toLocaleDateString("en-US", { month: "long" });
      const day = formatOrdinal(date.getDate());
      return `${month} ${day}`;
    }
    case SubscriptionDateFormat.ABBREVIATED: {
      const month = date.toLocaleDateString(undefined, { month: "short" });
      const day = formatOrdinal(date.getDate());
      return `${month} ${day}`;
    }
    case SubscriptionDateFormat.RELATIVE: {
      const until = Temporal.Now.plainDateTimeISO().until(
        dateToCurrentPlainDateTime(date),
      );
      const unit = until.days >= 14 ? "week" : "day";
      const values = { day: until.days, week: Math.round(until.days / 7) };
      const rtf = new Intl.RelativeTimeFormat("en", {
        numeric: "auto", // cool built-in feature, e.g., -1 days becomes "yesterday"
        style: "long",
      });
      return rtf.format(values[unit], unit);
    }
  }
}

export const delayOptionSchema = z.object({
  timeUnit: subscriptionDelayTimeUnitSchema,
  value: z.number().int().positive(),
});
export type SubscriptionDelayOption = z.infer<typeof delayOptionSchema>;

export enum SubscriptionSkipOrDelayState {
  DELAYED = "delayed",
  SKIPPED = "skipped",
  CHARGE_ALREADY_PROCESSED = "charge-already-processed",
}

export const INTERACTIVE_EMAIL_SUBSCRIPTION_LOG_PREFIX =
  "Interactive email product subscription";

export const RECHARGE_HOOKDECK_URL =
  "https://webhooks.getredo.dev/gzqjy0bgthshqa";

export const RECHARGE_WEBHOOK_TOPICS = {
  CHARGE_UPCOMING: "charge/upcoming",
} as const;
